import _                                                                        from 'underscore';
import { v4 as uuid }                                                           from 'uuid';
import React, { FC, MutableRefObject, useContext, useEffect, useRef, useState } from 'react';
import { Button, Divider, Form, Header, Icon, List, Segment, SemanticICONS }    from 'semantic-ui-react';
import { Formik, FormikConfig, FormikProps }                                    from 'formik';
import * as Yup                                                                 from 'yup'

import { SystemAccountingQueries }                                                             from '../../../../queries';
import { DynamicComponentsContext }                                                            from '../../../contexts';
import { CenteredContentContainer, ColoredClosableHeader, Flexbox, FlexItem, StyledContainer } from '../../../containers';
import { QueryOpDialog }                                                                       from '../../../components/from-query/QueryOpDialog';
import { Checkbox }                                                                            from '../../../components/forms';
import { BasicDialog }                                                                         from '../../../components/modals/BasicDialog';
import { TimeService }                                                                         from '../../../../services';
import { BasicDialogContainer }                                                                from '../../../components/modals/BasicDialogContainer';
import { HelpAndSupportStatement }                                                             from '../../../components/util/messaging/HelpAndSupportStatement';

import { useCurrentUser, useFeatureFlags }                   from '../../../../hooks';
import { ORGANIZATIONAL_ROLES, PROJECT_ROLES, SYSTEM_ROLES } from '../../../../constants/ProductEnums';
import { ProjectContext }                                    from '../ProjectIndexPage';

import { CorvalReportCreateCommandInput, CorvalReportDataSet, CorvalReportDocument, CorvalReportFormatType, FlexAlign, FlexFlow, FlexJustify, SemanticUIColor, Size, Uuid } from '../../../../types';


enum EXPORTABLE_DATASETS {
    PROJECTNAME                       = 'PROJECTNAME',
    PROJECTDETAILS                    = 'PROJECTDETAILS',
    KEYPLANNINGASSUMPTIONS            = 'KEYPLANNINGASSUMPTIONS',
    PROJECTBASEDDETAILS               = 'PROJECTBASEDDETAILS',
    COMMERCIALIZATIONOBJECTIVESBYYEAR = 'COMMERCIALIZATIONOBJECTIVESBYYEAR',
    COMMERCIALIZATIONDECISIONS        = 'COMMERCIALIZATIONDECISIONS',
    ASSUMPTIONGROUPSUMMARIES          = 'ASSUMPTIONGROUPSUMMARIES',
    // OBJECTIVESBYGROUP                 = 'OBJECTIVESBYGROUP',
    OBJECTIVESBYCATEGORY              = 'OBJECTIVESBYCATEGORY',
    OBJECTIVESBYYEARANDCATEGORY       = 'OBJECTIVESBYYEARANDCATEGORY',
    PROJECTACTIVITIES                 = 'PROJECTACTIVITIES',
    OBJECTIVEPROGRESSION              = 'OBJECTIVEPROGRESSION',
    BUDGETBYCATEGORY                  = 'BUDGETBYCATEGORY',
}

enum EXPORTABLE_FILE_TYPES {
    PPT = 'POWERPOINT',
    XLS = 'EXCEL',
    PDF = 'PDF',
}

interface ExportableDatasetInfoItem {
    LABEL: string,
    FILE_TYPES: EXPORTABLE_FILE_TYPES[],
    ACCESSIBLE_ROLES: {
        SYSTEM: null | SYSTEM_ROLES[],
        ORGANIZATION: null | ORGANIZATIONAL_ROLES[],
        PROJECT: null | PROJECT_ROLES[],
    }
}

const EXPORTABLE_DATASETS_INFO: Record<EXPORTABLE_DATASETS, ExportableDatasetInfoItem> = {
    [ EXPORTABLE_DATASETS.PROJECTNAME ]:                       {
        LABEL:            'Project Name',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.PROJECTDETAILS ]:                    {
        LABEL:            'Project Details',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.KEYPLANNINGASSUMPTIONS ]:           {
        LABEL:            'Key Planning Assumptions',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.PROJECTBASEDDETAILS ]:               {
        LABEL:            'Project Assumptions',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.COMMERCIALIZATIONOBJECTIVESBYYEAR ]: {
        LABEL:            'Objective Yearly Overviews',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.COMMERCIALIZATIONDECISIONS ]:        {
        LABEL:            'Commercialization Decisions',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.ASSUMPTIONGROUPSUMMARIES ]:          {
        LABEL:            'Assumption Group Summaries',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    /*[ EXPORTABLE_DATASETS.OBJECTIVESBYGROUP ]:                 {
     LABEL:            'Objectives By Group',
     FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
     ACCESSIBLE_ROLES: {
     SYSTEM:       null,
     ORGANIZATION: null,
     PROJECT:      null,
     }
     },*/
    [ EXPORTABLE_DATASETS.OBJECTIVESBYCATEGORY ]:        {
        LABEL:            'Objectives By Category',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.OBJECTIVEPROGRESSION ]:        {
        LABEL:            'Objective Progress',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.OBJECTIVESBYYEARANDCATEGORY ]: {
        LABEL:            'Objectives By Year and Category',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.BUDGETBYCATEGORY ]:            {
        LABEL:            'Budget by Category',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.PPT ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       null,
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    [ EXPORTABLE_DATASETS.PROJECTACTIVITIES ]:           {
        LABEL:            'Project Activities',
        FILE_TYPES:       [ EXPORTABLE_FILE_TYPES.XLS ],
        ACCESSIBLE_ROLES: {
            SYSTEM:       [ SYSTEM_ROLES.CORVALADMIN, SYSTEM_ROLES.DECISIONENGINEADMIN ],
            ORGANIZATION: null,
            PROJECT:      null,
        }
    },
    
}

type FileFormatComponentPropMapItem = {
    icon: SemanticICONS,
    color: SemanticUIColor,
    label: string
}

const fileFormatComponentPropMap: Record<EXPORTABLE_FILE_TYPES, FileFormatComponentPropMapItem> = {
    [ EXPORTABLE_FILE_TYPES.PPT ]: { icon: 'file outline powerpoint' as SemanticICONS, color: SemanticUIColor.PINK, label: 'PPT' },
    [ EXPORTABLE_FILE_TYPES.XLS ]: { icon: 'file outline excel' as SemanticICONS, color: SemanticUIColor.GREEN, label: 'XLS' },
    [ EXPORTABLE_FILE_TYPES.PDF ]: { icon: 'file outline pdf' as SemanticICONS, color: SemanticUIColor.RED, label: 'PDF' },
};

interface Props {

    /* The project to export */
    projectId: Uuid,

    /* File types available */
    initialTargetFileType?: EXPORTABLE_FILE_TYPES,

    /* Named data groups to export */
    initialTargetDataSets?: EXPORTABLE_DATASETS[],

    hideOptionsForFileTypes?: EXPORTABLE_FILE_TYPES[],

    hiddenStaticFileTypeDataSets?: Record<EXPORTABLE_FILE_TYPES, EXPORTABLE_DATASETS[]>

}

interface ExportProjectFormikValues {
    targetFileType: EXPORTABLE_FILE_TYPES,
    targetDataSets: {
        -readonly [P in keyof typeof EXPORTABLE_DATASETS]: boolean
    }
}

type ExportProjectFormikConfig = FormikConfig<ExportProjectFormikValues>

const defaultProps: Props = {
    projectId:                    null,
    initialTargetFileType:        EXPORTABLE_FILE_TYPES.PPT,
    initialTargetDataSets:        [
        EXPORTABLE_DATASETS.PROJECTNAME,
    ],
    hideOptionsForFileTypes:      [
        EXPORTABLE_FILE_TYPES.PPT,
        EXPORTABLE_FILE_TYPES.XLS,
    ],
    hiddenStaticFileTypeDataSets: {
        [ EXPORTABLE_FILE_TYPES.PPT ]: [
            EXPORTABLE_DATASETS.PROJECTNAME,
            EXPORTABLE_DATASETS.PROJECTDETAILS,
            EXPORTABLE_DATASETS.PROJECTBASEDDETAILS,
            EXPORTABLE_DATASETS.KEYPLANNINGASSUMPTIONS,
            EXPORTABLE_DATASETS.OBJECTIVEPROGRESSION,
            EXPORTABLE_DATASETS.ASSUMPTIONGROUPSUMMARIES,
            EXPORTABLE_DATASETS.COMMERCIALIZATIONOBJECTIVESBYYEAR,
            EXPORTABLE_DATASETS.COMMERCIALIZATIONDECISIONS,
            EXPORTABLE_DATASETS.BUDGETBYCATEGORY,
            EXPORTABLE_DATASETS.OBJECTIVESBYCATEGORY,
            EXPORTABLE_DATASETS.OBJECTIVESBYYEARANDCATEGORY
        ],
        [ EXPORTABLE_FILE_TYPES.XLS ]: [
            EXPORTABLE_DATASETS.PROJECTACTIVITIES,
        ],
        [ EXPORTABLE_FILE_TYPES.PDF ]: [],
    }

}

const ExportProjectDialog: FC<Props> = ( props ) => {

    const optionsHiddenForFileType = ( targetFileType: EXPORTABLE_FILE_TYPES ) => {

        return props.hideOptionsForFileTypes.includes( targetFileType );
    }

    const findHiddenFileTypeOptions = ( targetFileType: EXPORTABLE_FILE_TYPES ) => {

        return props.hiddenStaticFileTypeDataSets[ targetFileType ] ?? []
    }

    const initialTargetDataSets = optionsHiddenForFileType( props.initialTargetFileType )
                                  ? findHiddenFileTypeOptions( props.initialTargetFileType )
                                  : props.initialTargetDataSets;

    let { currentUser }                                                     = useCurrentUser(),
        { projectId, organizationId }                                       = useContext( ProjectContext ),
        { modal: [ modalContainer, modalActions ] }                         = useContext( DynamicComponentsContext ),
        { toasts: [ , toastActions ] }                                      = useContext( DynamicComponentsContext ),
        [ targetDataSets, setTargetDataSets ]                               = useState<EXPORTABLE_DATASETS[]>( initialTargetDataSets ),
        [ targetDataSetSupport, setTargetDataSetSupport ]                   = useState<EXPORTABLE_DATASETS[]>( [] ),
        [ targetFileType, setTargetFileType ]                               = useState<EXPORTABLE_FILE_TYPES>( props.initialTargetFileType ),
        [ fileTypesDisabled, setFileTypesDisabled ]                         = useState<EXPORTABLE_FILE_TYPES[]>( [] ),
        [ fileTypesSupported, setFileTypeSupport ]                          = useState<EXPORTABLE_FILE_TYPES[]>( [] ),
        [ reportData, setReportData ]                                       = useState<CorvalReportDocument>( null ),
        formikRef: MutableRefObject<FormikProps<ExportProjectFormikValues>> = useRef(),
        featureFlags                                                        = useFeatureFlags();

    useEffect( () => {

        // set the supported types available to the user
        setFileTypeSupport( getSupportedFileTypes() );

        // set the disabled types that may be shown
        setFileTypesDisabled( _.difference( _.values( EXPORTABLE_FILE_TYPES ), getSupportedFileTypes() ) )

        // set initial data sets based on inspection of current filetype
        setTargetDataSetSupport( getSupportedDataSets() );

        // set the file type to a supported one if possible
        if( !getSupportedFileTypes().includes( props.initialTargetFileType ) ){
            setTargetFileType( defaultProps.initialTargetFileType );
        }

        // set the initial target data sets to those we can actually support
        setTargetDataSets( _.intersection( targetDataSets, getSupportedDataSets() ) );

    }, [] );

    useEffect( () => {

        // kick state
        setTargetDataSetSupport( getSupportedDataSets() );

    }, [ targetFileType ] );

    useEffect( () => {

        if( formikRef.current ){

            // Get the current datasets
            let datasets = formikRef.current.values.targetDataSets;

            // If the target file type is hidden, we need to set the form values to only include the hidden options
            if( optionsHiddenForFileType( targetFileType ) ){

                // get the hidden options
                const staticOptions = findHiddenFileTypeOptions( targetFileType );

                // create a replica record to edit
                datasets = _.clone( formikRef.current.values.targetDataSets );

                // set the values to the hidden options
                _.map( targetDataSetSupport, ( value ) => {
                    return datasets[ value ] = staticOptions.includes( value );
                } );
            }

            formikRef.current.resetForm();
            formikRef.current.setValues( {
                targetFileType: targetFileType,
                targetDataSets: datasets,
            } );
        }

    }, [ targetDataSetSupport ] );

    useEffect( () => {

        if( !_.isNull( reportData ) ){
            modalActions.setProps( { ...modalContainer.props, size: 'tiny' } );
        }

    }, [ reportData ] )

    const getGraphAttributes = ( values: ExportProjectFormikValues ): { variables: { input: CorvalReportCreateCommandInput } } => {

        // get file type selected, and dataset inclusion values
        let { targetFileType, targetDataSets } = values;

        // all enum keys that exist so we can send to GraphQL
        let selectedDataSets = _.pairs( targetDataSets )
                                .filter( ( [ key, value ] ) => value )
                                .map( ( [ key, value ] ) => key );

        // ensure something gets sent, default to project name
        if( _.isEmpty( selectedDataSets ) ){

            switch( values.targetFileType ){

                case EXPORTABLE_FILE_TYPES.PPT:
                case EXPORTABLE_FILE_TYPES.PDF:
                    selectedDataSets.push( EXPORTABLE_DATASETS.PROJECTNAME );
                    break;

                case EXPORTABLE_FILE_TYPES.XLS:
                    selectedDataSets.push( EXPORTABLE_DATASETS.PROJECTACTIVITIES );
                    break;

                default:
                    throw new Error( 'No default data set for file type' );
            }
        }

        // send back the attributes to graph to use for the call
        return {
            variables: {
                input: {
                    id:               uuid(),
                    projectId:        props.projectId,
                    reportFormatType: targetFileType as unknown as CorvalReportFormatType,
                    targetDataSets:   selectedDataSets as unknown as CorvalReportDataSet[]
                }
            }
        }
    }

    const getSupportedDataSets = ( fileType: EXPORTABLE_FILE_TYPES = null ) => {

        return _.chain( EXPORTABLE_DATASETS )
                .omit( ( value ) => {

                    let supported = EXPORTABLE_DATASETS_INFO[ value ].FILE_TYPES.includes( fileType || targetFileType );
                    let accessibleRoles = _.clone( EXPORTABLE_DATASETS_INFO[ value ].ACCESSIBLE_ROLES );
                    let accessible = true;

                    /* Does user have a system role ?*/
                    if( _.isArray( accessibleRoles.SYSTEM ) ){
                        accessible = accessibleRoles.SYSTEM.includes( currentUser.systemRole as unknown as SYSTEM_ROLES );
                    }

                    /* Does user have an organizational role ?*/
                    if( accessible && _.isArray( accessibleRoles.ORGANIZATION ) ){
                        let userOrgRole = _.findWhere( currentUser.organizations, { organizationId } ) ?? null;
                        if( userOrgRole ){
                            accessible = accessibleRoles.ORGANIZATION.includes( userOrgRole as unknown as ORGANIZATIONAL_ROLES );
                        }
                    }

                    /* Does user have a project role ?*/
                    if( accessible && _.isArray( accessibleRoles.PROJECT ) ){
                        let userProjectRole = _.findWhere( currentUser.projects, { projectId } ) ?? null;
                        if( userProjectRole ){
                            accessible = accessibleRoles.PROJECT.includes( userProjectRole as unknown as PROJECT_ROLES );
                        }
                    }

                    /* Is feature flag enabled for xls export ?*/
                    if( accessible && fileType == EXPORTABLE_FILE_TYPES.XLS ){
                        accessible = featureFlags.enableExportProjectXlsTool;
                    }

                    return !supported || !accessible;
                } )
                .keys()
                .value() as EXPORTABLE_DATASETS[];
    }

    const getSupportedFileTypes = () => {

        return _.chain( EXPORTABLE_FILE_TYPES )
                .omit( ( fileType: EXPORTABLE_FILE_TYPES ) => {

                    let supported = _.chain( EXPORTABLE_DATASETS_INFO )
                                     .pick( getSupportedDataSets( fileType ) )
                                     .pluck( 'FILE_TYPES' )
                                     .flatten()
                                     .uniq()
                                     .contains( fileType )
                                     .value();

                    return !supported;
                } )
                .values()
                .value() as EXPORTABLE_FILE_TYPES[];
    }

    const generateDataSetFormValues = (): ExportProjectFormikValues['targetDataSets'] => {

        let supportedDataSets = getSupportedDataSets(),
            dataSetKeys       = _.values( supportedDataSets ),
            formValues        = {};

        dataSetKeys.map( setName => {

            formValues[ setName ] = targetDataSets.includes( EXPORTABLE_DATASETS[ setName ] )

        } )

        return ( formValues as ExportProjectFormikValues['targetDataSets'] );
    }

    const handleReportComplete = ( responseData: { createReport: CorvalReportDocument } ) => {

        let { createReport: { reportUrl } } = responseData;

        if( reportUrl ){

            modalActions.setProps( { ...modalContainer.props, size: 'mini' } );

            setReportData( responseData.createReport );

        } else {

            toastActions.error(
                'File Generation Issue',
                <>There was no link to the generated report! <HelpAndSupportStatement suggestTryAgain={ false } withClickableLink={ false } contactName={ null }/></>,
                { timeoutMS: 0 }
            );
        }
    }

    const handleReportError = ( response: any ) => {

        // toastActions.error( response.errors.message, { timeoutMS: 0 } );
    }

    const generateUrlWithTimestamp = ( url: string ) => {

        return `${ url }&ts=${ TimeService.getTimestamp() }`;
    }

    const handleFileTypeSelected = ( value: EXPORTABLE_FILE_TYPES ) => {

        setTargetFileType( value );
    }

    const handleFileDownloadClicked = ( url: string ) => {

        window.open( generateUrlWithTimestamp( url ) );
    }

    const handleDialogClosed = () => {

        modalActions.toggle( false );
    }

    const handleResetReportingDialog = () => {

        modalActions.setProps( { ...modalContainer.props, size: 'tiny' } );
        setTargetFileType( targetFileType );
        setTargetDataSets( targetDataSets );
        setReportData( null );
    }

    let formikProps: ExportProjectFormikConfig = {
        onSubmit:           null,
        enableReinitialize: true,
        validationSchema:   Yup.object().shape( {
            targetDataSets: Yup.object().test(
                'minOptionsSelected',
                'At least one data set is required',
                ( value ) => {

                    let itemsSelected = _.chain( value ).values().compact().value();

                    return ( itemsSelected.length > 0 )
                },
            )
        } ),
        initialValues:      {
            targetFileType,
            targetDataSets: generateDataSetFormValues()
        }
    };

    // if we have a reportData object...
    let reportDetails = reportData ? {
        url:            reportData.reportUrl,
        fileDisplay:    fileFormatComponentPropMap[ reportData.reportFormatType ] as FileFormatComponentPropMapItem,
        chosenDataSets: reportData.targetDataSets.map( set => EXPORTABLE_DATASETS_INFO[ set ].LABEL )
    } : {};

    return (

        reportData ? (

            <BasicDialog
                submitLabel={ 'Done' }
                cancelLabel={ 'New Export...' }
                onSubmit={ handleDialogClosed }
                successToastLabel={ null }
                onClose={ handleResetReportingDialog }
                errorToastLabel={ null }
                autoClose={ false }
                className={ 'no-padding' }>

                <BasicDialogContainer title={ 'Export File Created' } onClose={ handleDialogClosed }>

                    <StyledContainer padding={ Size.MD }>

                        <Segment secondary>

                            <StyledContainer padding={ Size.MD }>


                                <Segment raised>


                                    <StyledContainer margin={ [ Size.MD, null, null, null ] }>

                                        <List relaxed>
                                            <List.Item>
                                                <CenteredContentContainer>

                                                    <Icon color={ reportDetails.fileDisplay.color }
                                                          name={ reportDetails.fileDisplay.icon }
                                                          size={ 'huge' }
                                                    />
                                                </CenteredContentContainer>

                                            </List.Item>
                                            { reportDetails.chosenDataSets && (

                                                <List.Item>
                                                    <StyledContainer padding={ [ null, Size.MD, Size.MD, Size.MD ] }>
                                                        <p className={ 'text-center' }>
                                                            <strong>Prepared document contains:</strong>
                                                        </p>
                                                        <List>
                                                            { reportDetails.chosenDataSets.map( setLabel => (
                                                                    <List.Item key={ setLabel }>
                                                                        <List.Icon name={ 'check' } color={ 'teal' }/>
                                                                        <List.Content>{ setLabel }</List.Content>
                                                                    </List.Item>
                                                                )
                                                            ) }
                                                        </List>
                                                    </StyledContainer>
                                                </List.Item>
                                            ) }
                                            <List.Item>

                                                <Button fluid
                                                        color={ 'teal' }
                                                        size={ 'large' }
                                                        icon={ 'download' }
                                                        onClick={ () => handleFileDownloadClicked( reportDetails.url ) }
                                                        content={ 'Download File' }
                                                        className={ 'text--no-wrap' }
                                                />

                                            </List.Item>

                                        </List>

                                    </StyledContainer>


                                </Segment>

                            </StyledContainer>

                        </Segment>

                    </StyledContainer>

                </BasicDialogContainer>

            </BasicDialog>

        ) : (

            <Formik { ...formikProps } innerRef={ formikRef }>

                { formik => {

                    const { targetFileType } = formik.values,
                          hideOptions        = optionsHiddenForFileType( targetFileType );

                    return (

                        <QueryOpDialog
                            mutation={ SystemAccountingQueries.mutations.EXPORT_PROJECT_DATA() }
                            graphArgs={ () => getGraphAttributes( formik.values ) }
                            submitLabel={ 'Export' }
                            onError={ handleReportError }
                            onComplete={ handleReportComplete }
                            successToastLabel={ 'File Created Successfully!' }
                            operationInTransitMessage={ 'Generating File...' }
                            operationDisabled={ !formik.isValid }
                            className={ 'no-padding' }>

                            <ColoredClosableHeader title={ 'Export Project' } onClose={ handleDialogClosed }>

                                <StyledContainer padding={ Size.MD }>
                                    <Form>

                                        {/* FILE FORMAT */ }
                                        <Header as={ 'h5' }>File Type</Header>
                                        <p>Please select the export option that best suit your needs.</p>
                                        <Segment secondary>
                                            <Flexbox justify={ FlexJustify.CENTER } align={ FlexAlign.START }>

                                                { fileTypesSupported.map( value => {

                                                    let { icon, color, label } = fileFormatComponentPropMap[ value ],
                                                        disabled               = fileTypesDisabled.includes( value ),
                                                        isSelected             = ( formik.values.targetFileType === value );

                                                    return (

                                                        <FlexItem key={ value } noGrow basis={ 30 } className={ 'margin-xs-horizontal cursor--pointer' }>
                                                            <Segment raised color={ isSelected ? 'purple' : null } className={ disabled ? 'opacity--50' : '' }>

                                                                <Flexbox direction={ FlexFlow.COLS }
                                                                         align={ FlexAlign.CENTER }
                                                                         justify={ FlexJustify.START }
                                                                         onClick={ () => disabled ? null : handleFileTypeSelected( value ) }>

                                                                    <FlexItem>

                                                                        <Icon name={ icon as SemanticICONS }
                                                                              size={ 'huge' }
                                                                              color={ ( !isSelected || disabled ? 'grey' : color ) as SemanticUIColor }/>

                                                                    </FlexItem>

                                                                    <FlexItem className={ 'margin-sm-top' }>
                                                                        <Header as={ 'h5' } content={ label }/>

                                                                        {/* <Checkbox radio noWrap name={ value } disabled={ disabled } label={ label } checked={ isSelected }/> */ }

                                                                    </FlexItem>

                                                                </Flexbox>
                                                            </Segment>
                                                        </FlexItem>

                                                    )
                                                } ) }
                                            </Flexbox>
                                        </Segment>


                                        {/* DATA SETS */ }
                                        { !hideOptions && (
                                            <div>

                                                <Divider/>

                                                <Header as={ 'h5' }>Export Data</Header>
                                                <p>Please select what to include from the available list below.</p>

                                                <Flexbox cols={ 2 } gutterSize={ Size.XS }>

                                                    { targetDataSetSupport.map( dataSetKey => (

                                                        <FlexItem key={ dataSetKey }>

                                                            <Checkbox
                                                                id={ dataSetKey }
                                                                name={ `targetDataSets.${ dataSetKey }` }
                                                                label={ EXPORTABLE_DATASETS_INFO[ dataSetKey ].LABEL }/>

                                                        </FlexItem>

                                                    ) ) }
                                                </Flexbox>
                                            </div>
                                        ) }

                                    </Form>

                                </StyledContainer>

                            </ColoredClosableHeader>

                        </QueryOpDialog>
                    )
                } }

            </Formik>

        ) )

};

ExportProjectDialog.defaultProps = defaultProps;

export { ExportProjectDialog, EXPORTABLE_DATASETS, EXPORTABLE_FILE_TYPES }
